import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { firebase_auth, firebase_firestore, firebase_functions } from '../common/firebaseConfig'
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore'
import { collection, doc, limit, query, where, getDocs } from 'firebase/firestore'
import { Pattern } from '../app/builder/contexts/PatternContext'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { useCallableRef } from '../api/useCallableRef'
import { ISymbol } from '../common/components/dialogs/symbolSelectDialog/SymbolSelect'
import { useSearchParams } from 'react-router-dom'
import { SnackbarHelperContext } from './SnackbarHelperContext'
import { Tabs } from '../layout/MainLayout'
import { ModalProvider } from './ModalProvider'
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'
import { BacktestProvider } from '../app/backtest/contexts/BacktestContext'
import dayjs from 'dayjs'
import ShareableDialog from '../components/ShareableDialog'
import PatternCopiedDialog from '../components/dialogs/PatternCopiedDialog'
import { getClientTimeZone } from '../common/timezones'
import { Schedule, normalizeSchedule } from '../common/backtestSchedule'
import {
  ALL_FEATURE_TAGS,
  FeatureTag,
  FeatureTags,
  isActiveStatus,
} from '../common/access/productFeatureTags'
import LoadingShell from '../components/LoadingShell'
import { TRADING_HOUSE_STORAGE_KEY } from '../common/utils/tradingHouseBranding'
import {
  AutoTraderUsage,
  countedAlerts,
  fitsAutoTradeLimits,
  getAutoTradeLimits,
  getAutoTraderUsage,
  limitsCoverAlert,
} from '../common/utils/autoTradeLimits'
import { captureEvent } from '../posthog'

TimeAgo.addDefaultLocale(en)
const clientTimezone = getClientTimeZone()

export type FixedOptions = {
  exitStrategy: 'fixed'
}

export type AtrOptions = {
  exitStrategy: 'atr'
  atrPeriod: number
  atrChartPeriod: number
  atrChartTimeframe: string
}

export type PeakValleyOptions = {
  exitStrategy: 'peak_valley'
  fractalCount: number
  maxPeakValleyLookback: number
  priceOffset: number
}

export type IncrementalOptions = {
  exitStrategy: 'incremental'
  incrementalStart?: number
  incrementalStop?: number
  incrementalStep?: number
}

export type HighLowOptions = {
  exitStrategy: 'high_low'
  maxLookback: number
  priceOffset: number
}

export type ConditionalRatio = {
  risk: number
  reward: number
  patternId: string
  patternVersion: number
  gcid: string
  priority: number
}

export type TradeRatio = {
  risk: number
  reward: number
  conditions?: ConditionalRatio[]
}

export type BaseOptions = {
  tradeRatios: TradeRatio[]
  useLatency?: boolean
}

export type GenerateTradeOptions = BaseOptions &
  (FixedOptions | AtrOptions | PeakValleyOptions | IncrementalOptions)

type candleForceExit = {
  type: 'candles'
  count: number
}

type timeForceExit = {
  type: 'time'
  periodicity: number
  timeframe: string
}

type AnyForceExit = candleForceExit | timeForceExit
type ForceExitType<T, K> = Omit<Extract<T, { type: K }>, 'type'>
type ForceExit = {
  [K in AnyForceExit['type']]?: ForceExitType<AnyForceExit, K>
}

export type AnalyzeTradeOptions = {
  equity?: number
  contractSize?: number
  riskPerTrade: number
  commissions?: number
  /** Broker spread in points. null or absent uses the spread the tick data carries. */
  spread?: number | null
  tradeLimit?: number
  dailyTradeLimit?: number
  allowHedging?: boolean
  maxRisk?: number
  forceExit?: ForceExit
  dynamicTakeProfit?: {
    type: 'atr'
    allowIncrease: boolean
    allowDecrease: boolean
    atrRecalculationPeriod: number
    atrRecalculationTimeframe: string
    atrPeriod: number
    atrChartPeriod: number
    atrChartTimeframe: string
  }
}

const defaultATRExitStrategy: AtrOptions = {
  exitStrategy: 'atr',
  atrChartPeriod: 5,
  atrChartTimeframe: 'minute',
  atrPeriod: 15,
}

const defaultIncrementalExitStrategy: IncrementalOptions = {
  exitStrategy: 'incremental',
  incrementalStart: 0.0005,
  incrementalStop: 0.0001,
  incrementalStep: 0.0001,
}

const defaultPeakValleyExitStrategy: PeakValleyOptions = {
  exitStrategy: 'peak_valley',
  fractalCount: 2,
  maxPeakValleyLookback: 10,
  priceOffset: 0.0001,
}

const defaultHighLowExitStrategy: HighLowOptions = {
  exitStrategy: 'high_low',
  maxLookback: 10,
  priceOffset: 0.0001,
}

const defaultFixedExitStrategy: FixedOptions = {
  exitStrategy: 'fixed',
}

export const defaultInputSettings: any = {
  name: 'Custom Settings',
  atrPeriod: 5,
  dateRange: {
    startDate: new Date(new Date().getTime() - 1440 * 60 * 1000 * 30).setHours(0, 0, 0, 0),
    endDate: new Date().setHours(23, 59, 59, 999),
  },
  schedule: {
    timezone: clientTimezone,
    windows: [
      { dayOfWeek: 1, startTime: '00:00', endTime: '24:00' },
      { dayOfWeek: 2, startTime: '00:00', endTime: '24:00' },
      { dayOfWeek: 3, startTime: '00:00', endTime: '24:00' },
      { dayOfWeek: 4, startTime: '00:00', endTime: '24:00' },
      { dayOfWeek: 5, startTime: '00:00', endTime: '24:00' },
    ],
  },
  generateTrades: {
    ...defaultIncrementalExitStrategy,
    ...defaultPeakValleyExitStrategy,
    ...defaultFixedExitStrategy,
    ...defaultATRExitStrategy,
    ...defaultHighLowExitStrategy,
    exitStrategy: 'atr',
    tradeRatios: [],
  },
  analyzeTrades: {
    equity: 10000,
    riskPerTrade: 0.01,
    commissions: 2,
    tradeLimit: 0,
    dailyTradeLimit: 0,
    allowHedging: false,
    forceExit: {},
    contractSize: 100000,
  },
  isPublic: true,
}

const normalize = (value?: string) => value?.toLowerCase().trim()

const resolveFeatureTag = (rawTag?: string): FeatureTag | undefined => {
  const tag = normalize(rawTag)
  if (!tag) return undefined
  return ALL_FEATURE_TAGS.find((feature) => feature.toLowerCase() === tag) as FeatureTag | undefined
}

const computeEffectiveTags = (options: {
  status?: string
  productTags?: FeatureTag[]
  productDenyTags?: FeatureTag[]
  userTags?: FeatureTag[]
  denyTags?: FeatureTag[]
  isAdmin?: boolean
  tradingProductEnabled?: boolean
  gtfStatus?: string
}) => {
  const normalizedStatus = normalize(options.status)
  if (normalizedStatus === 'canceled' || normalizedStatus === 'hold') {
    return new Set<FeatureTag>([])
  }

  const effective = new Set<FeatureTag>()

  if (isActiveStatus(options.gtfStatus)) {
    effective.add(FeatureTags.DTU_INDICATORS)
  }

  // Product defaults: add tags then remove denyTags
  ;(options.productTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.add(f)
  })
  ;(options.productDenyTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.delete(f)
  })

  // User overrides: add user tags, then remove user denyTags
  ;(options.userTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.add(f)
  })

  if (options.tradingProductEnabled) {
    effective.add(FeatureTags.AUTOTRADING)
    effective.add(FeatureTags.MANAGE_TRADING_ACCOUNTS)
  }

  ;(options.denyTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.delete(f)
  })

  return effective
}

export type BrokerUserInputs = {
  symbol?: string
}

export type Alert = {
  id: string
  name: string
  pattern?: Pattern
  autoUpdatePattern?: boolean
  alertTemplateId?: string
  symbol?: string
  timeRanges: {
    startTimeRange: string
    endTimeRange: string
    key?: number
    allowableCount?: number
  }[]
  timezone: string
  daysOfWeek: number[]
  schedule?: Schedule
  alertTypes: ('sms' | 'email' | 'app' | 'webhook' | 'exitTradingPlan')[]
  user?: {
    gcid: string
    email: string
    phoneNumber: string
  }
  active: boolean
  from?: 'pattern' | 'backtest'
  backtestId?: string
  backtestGcid?: string

  trading?: {
    enabled?: boolean // Per-alert trading access override
    accountId?: string
    exitTradingPlanId?: string
    options: {
      generateTrades: GenerateTradeOptions
      analyzeTrades: AnalyzeTradeOptions
    }
    broker?: BrokerUserInputs
  }
}

export const defaultAlert: Alert = {
  id: '',
  name: 'Custom Alert',
  alertTypes: ['email'],
  timeRanges: [],
  timezone: clientTimezone,
  daysOfWeek: [1, 2, 3, 4, 5],
  schedule: {
    timezone: clientTimezone,
    windows: [1, 2, 3, 4, 5].map((day) => ({
      dayOfWeek: day,
      startTime: '00:00',
      endTime: '24:00',
    })),
  },
  active: true,
}

export type BacktestSortType = {
  field: string
  direction: 'asc' | 'desc'
}

export type BacktestFilterType = {
  symbolSearch?: string
  minProfitPercentage: number
  maxProfitPercentage: number
  minProfitDollars: number
  maxProfitDollars: number
  minAbsoluteDrawdownPercentage: number
  maxAbsoluteDrawdownPercentage: number
  minRelativeDrawdownPercentage: number
  maxRelativeDrawdownPercentage: number
  minDateRange: number
  maxDateRange: number
  daysBetween?: number
  patternNameSearch: string
  triggerChart?: {
    periodicity?: number
    timeframe?: string
  }
}

export const backtestFilterDefaultValues: Record<keyof BacktestFilterType, any> = {
  symbolSearch: undefined,
  minProfitPercentage: Number.MIN_SAFE_INTEGER,
  maxProfitPercentage: Number.MAX_SAFE_INTEGER,
  minProfitDollars: Number.MIN_SAFE_INTEGER,
  maxProfitDollars: Number.MAX_SAFE_INTEGER,
  minAbsoluteDrawdownPercentage: Number.MIN_SAFE_INTEGER,
  maxAbsoluteDrawdownPercentage: Number.MAX_SAFE_INTEGER,
  minRelativeDrawdownPercentage: Number.MIN_SAFE_INTEGER,
  maxRelativeDrawdownPercentage: Number.MAX_SAFE_INTEGER,
  minDateRange: Number.MIN_SAFE_INTEGER,
  maxDateRange: Number.MAX_SAFE_INTEGER,
  daysBetween: undefined,
  patternNameSearch: '',
  triggerChart: undefined,
}

export type PatternFilterType = {
  symbol?: string
  name?: string
  timeframes?: string[]
  indicators?: string[]
}

type ApplicationSuiteContextType = {
  backtestSettingInputData: any
  setBacktestSettingInputData: React.Dispatch<React.SetStateAction<any | null>>
  activeTab: { tab: Tabs; options?: { item?: any; type?: string } }
  setActiveTab: (
    newTab:
      | { tab: Tabs; options?: { item?: any; type?: string } }
      | ((prev: { tab: Tabs; options?: { item?: any; type?: string } }) => {
          tab: Tabs
          options?: { item?: any; type?: string }
        })
  ) => void
  usermeta: any
  runningBacktests: boolean
  canShowcaseViewBacktestSettings: boolean
  canViewPrivatePatterns: boolean
  symbols: ISymbol[] | undefined
  tradingHouse: string
  setTradingHouse: React.Dispatch<React.SetStateAction<string>>
  canToggleTradingHouse: boolean
  tradingHouseObject: any
  canPrivatePatterns: boolean
  canViewBacktestInsights: boolean
  setShowReportCardDateTimeTooltips: React.Dispatch<React.SetStateAction<boolean>>
  showReportCardDateTimeTooltips: boolean
  isCreateAlertModalOpen: boolean
  setIsCreateAlertModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  savedBacktests: any[]
  savedBacktestsMap: { [key: string]: any }
  canCreateAlerts: boolean
  canActivateAlerts: boolean
  canEditAlertTimeframes: boolean
  canViewFeaturedBacktests: boolean
  backtestToView: any
  setBacktestToView: React.Dispatch<React.SetStateAction<any>>
  canAutotradeAlerts: boolean
  canViewShowcaseApp: boolean
  canViewPatternBuilderApp: boolean
  canViewAlertApp: boolean
  closeBacktestDialog: () => void
  setCloseBacktestDialog: React.Dispatch<React.SetStateAction<() => void>>
  hasFeature: (feature: FeatureTag) => boolean
  setAlertAutoTradeEnabled: (data: { alertId: string; enabled: boolean }) => Promise<any>
  autoTraderUsage: AutoTraderUsage
  // Already auto-trading, a per-alert grant, or the feature flag with slots that cover this alert
  canAutotradeAlert: (alert: Alert) => boolean
  // Whether turning auto-trade on for this alert fits the user's limits
  canEnableAutoTrader: (alert: Alert) => boolean
  backtestNameOverrides: Record<string, string>
  renameBacktestLocally: (backtestId: string, name: string) => void
  alerts: Alert[]
  // Opens the "pattern copied" dialog for a pattern just saved into the user's account
  setCopiedPattern: React.Dispatch<React.SetStateAction<Pattern | null>>
}

const ApplicationSuiteContext = createContext<ApplicationSuiteContextType | undefined>(undefined)

type ApplicationSuiteProviderProps = {
  children: ReactNode
}

function ApplicationSuiteProvider({ children }: ApplicationSuiteProviderProps) {
  const [user] = useAuthState(firebase_auth)
  const [usermeta, usermetaLoading] = useDocumentData<any>(
    doc(firebase_firestore, `usermeta/${user?.uid}`)
  )
  const [runningBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta?.gcid}/backtests`),
      where('status', '==', 'running'),
      limit(1)
    )
  )
  const [getSymbols] = useHttpsCallable(firebase_functions, 'getSymbols')
  const [searchParams] = useSearchParams()
  const [consumeShareable] = useHttpsCallable(firebase_functions, 'consumeShareable')
  const [setAlertAutoTradeEnabledCallable] = useCallableRef('setAlertAutoTradeEnabled')
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [activeTab, setActiveTab] = useState<{
    tab: Tabs
    options?: { item?: any; type?: string }
  }>({ tab: Tabs.Showcase })
  const activeTabRef = useRef(activeTab)
  const [backtestSettingInputData, setBacktestSettingInputData] =
    useState<any>(defaultInputSettings)
  const [symbols, setSymbols] = useState<ISymbol[]>()
  const [isCreateAlertModalOpen, setIsCreateAlertModalOpen] = useState<boolean>(false)
  const [showReportCardDateTimeTooltips, setShowReportCardDateTimeTooltips] =
    useState<boolean>(false)
  const [shareableData, setShareableData] = useState<any>(null)
  const [copiedPattern, setCopiedPattern] = useState<Pattern | null>(null)
  const [backtestToView, setBacktestToView] = useState<any>(null)
  const [closeBacktestDialog, setCloseBacktestDialog] = useState<() => void>(() => {})
  const [backtestNameOverrides, setBacktestNameOverrides] = useState<Record<string, string>>({})

  const renameBacktestLocally = useCallback((backtestId: string, name: string) => {
    setBacktestNameOverrides((prev) => ({ ...prev, [backtestId]: name }))
  }, [])

  const [canToggleTradingHouse, setCanToggleTradingHouse] = useState<boolean>(false)
  const [tradingHouse, setTradingHouse] = useState<string>('greenchart')
  const [tradingHouseObject] = useDocumentData(
    doc(firebase_firestore, `tradingHouses/${tradingHouse}`)
  )
  const [savedBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta?.gcid}/savedShowcase`),
      where('backtestSettings.tradingHouse', '==', tradingHouse)
    )
  ) as any[]
  const [alerts] = useCollectionData(
    query(
      collection(firebase_firestore, `tradingPlans`),
      where('user.gcid', '==', usermeta?.gcid || null)
    )
  ) as any[]
  const [productTagsFromDb, setProductTagsFromDb] = useState<FeatureTag[] | null>(null)
  const [productDenyTagsFromDb, setProductDenyTagsFromDb] = useState<FeatureTag[] | null>(null)

  const [savedBacktestsMap, setSavedBacktestsMap] = useState<{ [key: string]: any }>({})

  const membershipStatus: any = useMemo(
    () =>
      usermeta?.tradingHouses &&
      usermeta.tradingHouses[tradingHouseObject?.name]?.currentStatus?.status,
    [usermeta, tradingHouseObject]
  )
  const isAdmin = useMemo(() => usermeta?.isAdmin, [usermeta?.isAdmin])

  const userTags = useMemo(() => usermeta?.products?.tags || [], [usermeta?.products?.tags])
  const denyTags = useMemo(() => usermeta?.products?.denyTags || [], [usermeta?.products?.denyTags])

  const alertsProductName = useMemo(
    () => usermeta?.products?.alerts?.name,
    [usermeta?.products?.alerts?.name]
  )
  const normalizedAlertsProductName = useMemo(
    () => normalize(alertsProductName),
    [alertsProductName]
  )
  const autoTradeLimits = useMemo(
    () => getAutoTradeLimits(usermeta?.products?.alerts),
    [usermeta?.products?.alerts]
  )
  const tradingProductEnabled = useMemo(
    () => !!usermeta?.isAdmin || autoTradeLimits.dtu + autoTradeLimits.any > 0,
    [usermeta?.isAdmin, autoTradeLimits]
  )

  // Product tags are ready when either no product name exists (nothing to fetch)
  // or the fetch has completed (productTagsFromDb is an array, not the initial null)
  const productTagsReady = useMemo(
    () => !normalizedAlertsProductName || productTagsFromDb !== null,
    [normalizedAlertsProductName, productTagsFromDb]
  )

  useEffect(() => {
    ;(async () => {
      if (!normalizedAlertsProductName) {
        setProductTagsFromDb(null)
        setProductDenyTagsFromDb(null)
        return
      }
      try {
        const snap = await getDocs(
          query(
            collection(firebase_firestore, 'products'),
            where('name', '==', normalizedAlertsProductName),
            limit(1)
          )
        )
        if (snap.empty) {
          setProductTagsFromDb([])
          setProductDenyTagsFromDb([])
          return
        }
        const data = snap.docs[0].data() || {}
        const tags = (data.tags || []) as FeatureTag[]
        const denyTags = (data.denyTags || []) as FeatureTag[]
        setProductTagsFromDb(tags as FeatureTag[])
        setProductDenyTagsFromDb(denyTags as FeatureTag[])
      } catch (error) {
        console.error('Error fetching product tags from db', error)
        setProductTagsFromDb([])
        setProductDenyTagsFromDb([])
      }
    })()
  }, [normalizedAlertsProductName])

  const gtfStatus = useMemo(
    () => usermeta?.tradingHouses?.gtf?.currentStatus?.status,
    [usermeta?.tradingHouses?.gtf?.currentStatus?.status]
  )

  const effectiveTags = useMemo(
    () =>
      computeEffectiveTags({
        status: membershipStatus,
        productTags: productTagsFromDb || undefined,
        productDenyTags: productDenyTagsFromDb || undefined,
        userTags,
        denyTags,
        isAdmin,
        tradingProductEnabled,
        gtfStatus,
      }),
    [
      membershipStatus,
      userTags,
      denyTags,
      isAdmin,
      tradingProductEnabled,
      gtfStatus,
      productTagsFromDb,
      productDenyTagsFromDb,
    ]
  )

  const hasFeature = useCallback(
    (feature: FeatureTag) => effectiveTags.has(feature),
    [effectiveTags]
  )

  const maxAlertCount = !!usermeta?.products?.alerts
    ? usermeta?.products?.alerts.alertCount
    : isAdmin
      ? 10
      : 1

  const canShowcaseViewBacktestSettings = hasFeature(FeatureTags.BACKTEST_SETTINGS)
  const canViewPrivatePatterns = hasFeature(FeatureTags.PRIVATE_PATTERNS)
  const canPrivatePatterns = hasFeature(FeatureTags.PRIVATE_PATTERNS)
  const canViewBacktestInsights = hasFeature(FeatureTags.BACKTEST_INSIGHTS)

  const canAutotradeAlerts = hasFeature(FeatureTags.AUTOTRADING)

  // Still counted against the limits until the TPA pointer is retired
  const tpaAffiliateAlertId = usermeta?.tradingHouses?.gtf?.tpaAffiliateAlertId as
    | string
    | undefined

  const setAlertAutoTradeEnabled = useCallback(
    async (data: { alertId: string; enabled: boolean }) => {
      const result = await setAlertAutoTradeEnabledCallable(data)
      if ((result?.data as { status?: number } | undefined)?.status === 200) {
        captureEvent('alert_autotrade_toggled', { enabled: data.enabled })
      }
      return result
    },
    [setAlertAutoTradeEnabledCallable]
  )

  const autoTraderUsage = useMemo(
    () =>
      getAutoTraderUsage(
        countedAlerts((alerts ?? []) as Alert[], tpaAffiliateAlertId),
        autoTradeLimits
      ),
    [alerts, tpaAffiliateAlertId, autoTradeLimits]
  )
  const canAutotradeAlert = useCallback(
    (alert: Alert) =>
      // An alert already auto-trading keeps its controls so it can always be turned off
      !!alert.alertTypes?.includes('webhook') ||
      alert.trading?.enabled === true ||
      (canAutotradeAlerts && (!!isAdmin || limitsCoverAlert(alert, autoTradeLimits))),
    [canAutotradeAlerts, isAdmin, autoTradeLimits]
  )
  const canEnableAutoTrader = useCallback(
    (alert: Alert) =>
      !!isAdmin ||
      fitsAutoTradeLimits(alert, (alerts ?? []) as Alert[], autoTradeLimits, tpaAffiliateAlertId),
    [isAdmin, alerts, autoTradeLimits, tpaAffiliateAlertId]
  )

  const canViewShowcaseApp = hasFeature(FeatureTags.SHOWCASE)
  const canViewPatternBuilderApp = hasFeature(FeatureTags.PATTERN_BUILDER)
  const canViewAlertApp = hasFeature(FeatureTags.ALERTS)

  const canCreateAlerts =
    hasFeature(FeatureTags.ALERTS) &&
    hasFeature(FeatureTags.ALERT_CREATE) &&
    (alerts?.length || 0) < (isAdmin ? 100 : 50)

  const canActivateAlerts =
    hasFeature(FeatureTags.ALERTS) &&
    (alerts?.filter((alert: Alert) => alert.active).length || 0) < maxAlertCount

  const canEditAlertTimeframes =
    hasFeature(FeatureTags.ALERTS) && !hasFeature(FeatureTags.RESTRICTED_ALERT_TIMEFRAMES)

  const canViewFeaturedBacktests = hasFeature(FeatureTags.FEATURED_BACKTESTS)

  // Update ref when activeTab changes
  useEffect(() => {
    activeTabRef.current = activeTab
  }, [activeTab])

  // Wrapper function to enforce access controls when setting active tab
  const setActiveTabWithAccessControl = useCallback(
    (
      newTab:
        | { tab: Tabs; options?: { item?: any; type?: string } }
        | ((prev: { tab: Tabs; options?: { item?: any; type?: string } }) => {
            tab: Tabs
            options?: { item?: any; type?: string }
          })
    ) => {
      // If usermeta is still loading, don't allow any tab changes
      if (usermetaLoading) {
        return
      }

      const targetTab = typeof newTab === 'function' ? newTab(activeTabRef.current) : newTab

      // Check if user has access to the target tab
      let hasAccess = false

      switch (targetTab.tab) {
        case Tabs.Showcase:
          hasAccess = canViewShowcaseApp
          break
        case Tabs.PatternBuilder:
          hasAccess = canViewPatternBuilderApp
          break
        case Tabs.Alerts:
          hasAccess = canViewAlertApp
          break
        case Tabs.Profile:
          // Profile is always accessible
          hasAccess = true
          break
        default:
          // For any other tabs, allow access (they might be internal navigation)
          hasAccess = true
          break
      }

      // Only update the tab if user has access
      if (hasAccess) {
        setActiveTab(newTab)
      }
    },
    [usermetaLoading, canViewShowcaseApp, canViewPatternBuilderApp, canViewAlertApp]
  )

  const extractShareableId = useCallback(async () => {
    const shareableId = searchParams.get('shareable')
    return shareableId
  }, [searchParams])

  useEffect(() => {
    if (!usermeta) return

    defaultAlert.user = {
      phoneNumber: '',
      email: usermeta.alertEmail || usermeta.email || '',
      gcid: usermeta.gcid,
    }

    const defaultTime = '00:00'
    if (canEditAlertTimeframes) {
      defaultAlert.timeRanges = [{ startTimeRange: defaultTime, endTimeRange: defaultTime }]
    } else {
      if (hasFeature(FeatureTags.RESTRICTED_ALERT_TIMEFRAMES)) {
        defaultAlert.timeRanges = [
          { startTimeRange: defaultTime, endTimeRange: defaultTime, allowableCount: 5 },
        ]
      } else {
        // Define MT time ranges
        const mtRanges = [
          { start: '08:00', end: '12:00' }, // 8am mt - 12pm mt
          { start: '12:00', end: '16:00' }, // 12pm mt - 4pm mt
          { start: '16:00', end: '20:00' }, // 4pm mt - 8pm mt
        ]

        // Convert MT times to user's local time
        const userTimeRanges = mtRanges.map((range) => {
          const mtStart = dayjs()
            .tz('America/Denver')
            .set('hour', parseInt(range.start.split(':')[0]))
            .set('minute', parseInt(range.start.split(':')[1]))
          const mtEnd = dayjs()
            .tz('America/Denver')
            .set('hour', parseInt(range.end.split(':')[0]))
            .set('minute', parseInt(range.end.split(':')[1]))

          return {
            startTimeRange: mtStart.tz(clientTimezone).format('HH:mm'),
            endTimeRange: mtEnd.tz(clientTimezone).format('HH:mm'),
            allowableCount: 1,
          }
        })

        defaultAlert.timeRanges = userTimeRanges
      }
    }

    // Derive schedule from the feature-gated timeRanges so restricted users
    // get the correct windows (with allowableCount) rather than the static all-day default.
    // Strip the static schedule so normalizeSchedule uses the legacy-path
    // (timeRanges/daysOfWeek) rather than short-circuiting on the new-shape branch.
    defaultAlert.schedule = normalizeSchedule({ ...defaultAlert, schedule: undefined })

    // Rebuild rather than append: this effect re-runs on every usermeta emit and mutates
    // the shared defaultAlert, so appending accumulates a duplicate 'sms' on each run.
    const verifiedPhoneNumber =
      usermeta.alertPhoneNumber?.phoneNumber && usermeta.alertPhoneNumber?.verified
        ? usermeta.alertPhoneNumber.phoneNumber
        : usermeta.phoneNumber?.phoneNumber && usermeta.phoneNumber?.verified
          ? usermeta.phoneNumber.phoneNumber
          : ''

    defaultAlert.user.phoneNumber = verifiedPhoneNumber
    defaultAlert.alertTypes = verifiedPhoneNumber ? ['email', 'sms'] : ['email']
  }, [usermeta, canEditAlertTimeframes, hasFeature])

  useEffect(() => {
    if (usermeta) {
      const hasGreenChartAccess = ['member', 'free', 'trial'].includes(
        usermeta.tradingHouses?.greenchart?.currentStatus?.status
      )
      const hasGTFAccess = ['member', 'free', 'trial'].includes(
        usermeta.tradingHouses?.gtf?.currentStatus?.status
      )

      const canToggleTradingHouse =
        hasGTFAccess &&
        hasGreenChartAccess &&
        (usermeta.tradingHouses?.gtf?.currentStatus?.status === 'free' ||
          usermeta.canToggleTradingHouse ||
          hasFeature(FeatureTags.CAN_TOGGLE_TRADING_HOUSE))
      setCanToggleTradingHouse(canToggleTradingHouse)

      // A stored house only applies while the user can actually toggle; otherwise
      // their access decides it.
      const storedTradingHouse = canToggleTradingHouse
        ? localStorage.getItem(TRADING_HOUSE_STORAGE_KEY)
        : null
      const resolvedTradingHouse = storedTradingHouse || (hasGTFAccess ? 'gtf' : 'greenchart')

      setTradingHouse(resolvedTradingHouse)
      // Cached for the loading screen, which paints before usermeta loads and so
      // has no other way to know which house to brand itself as.
      localStorage.setItem(TRADING_HOUSE_STORAGE_KEY, resolvedTradingHouse)
    }
  }, [usermeta, hasFeature])

  useEffect(() => {
    if (!canActivateAlerts) {
      defaultAlert.active = false
    }
  }, [canActivateAlerts])

  useEffect(() => {
    getSymbols().then((symbols: any) => {
      setSymbols(symbols.data.map((symbol: any) => ({ ...symbol, name: symbol.displaySymbol })))
    })
  }, [getSymbols])

  useEffect(() => {
    setBacktestSettingInputData((prev: any) => ({
      ...prev,
      patternId: undefined,
    }))
  }, [tradingHouse])

  useEffect(() => {
    ;(async () => {
      const shareableId = await extractShareableId()

      if (shareableId) {
        if (shareableId.trim() === '') {
          return
        }

        const result: any = await consumeShareable({ id: shareableId })
        if (result?.data?.status >= 400) {
          setSnackbarSettings({
            message: result?.data?.message
              ? result.data.message
              : `There was a problem importing the shareable onto your account!`,
            severity: 'error',
            autoHideDuration: 6000,
          })
        } else {
          setShareableData(result?.data)
        }
      }
    })()
  }, [searchParams, usermeta?.email, consumeShareable, setSnackbarSettings, extractShareableId])

  // convert saved backtests to map
  useEffect(() => {
    if (!savedBacktests) return
    const map: { [key: string]: any } = {}
    savedBacktests.forEach((backtest: any) => {
      map[backtest.id] = backtest
    })
    setSavedBacktestsMap(map)
  }, [savedBacktests])

  useEffect(() => {
    // Only run this effect when usermeta and product tags are loaded to prevent jumping
    if (usermetaLoading || !productTagsReady) return

    // Determine which app the user should be redirected to based on their access
    let targetTab: Tabs

    if (canViewShowcaseApp) {
      targetTab = Tabs.Showcase
    } else if (canViewPatternBuilderApp) {
      targetTab = Tabs.PatternBuilder
    } else if (canViewAlertApp) {
      targetTab = Tabs.Alerts
    } else {
      targetTab = Tabs.Profile
    }
    setActiveTabWithAccessControl({ tab: targetTab })
  }, [
    usermetaLoading,
    productTagsReady,
    canViewShowcaseApp,
    canViewPatternBuilderApp,
    canViewAlertApp,
    setActiveTabWithAccessControl,
  ])

  const isRunningBacktests = (runningBacktests?.length || 0) > 0

  const contextValue = useMemo(
    () => ({
      canShowcaseViewBacktestSettings,
      canViewPrivatePatterns,
      canPrivatePatterns,
      activeTab,
      setActiveTab: setActiveTabWithAccessControl,
      usermeta,
      backtestSettingInputData,
      setBacktestSettingInputData,
      runningBacktests: isRunningBacktests,
      symbols,
      tradingHouse,
      setTradingHouse,
      canToggleTradingHouse,
      tradingHouseObject,
      canViewBacktestInsights,
      setShowReportCardDateTimeTooltips,
      showReportCardDateTimeTooltips,
      isCreateAlertModalOpen,
      setIsCreateAlertModalOpen,
      savedBacktests,
      savedBacktestsMap,
      canCreateAlerts,
      canActivateAlerts,
      canEditAlertTimeframes,
      canViewFeaturedBacktests,
      backtestToView,
      setBacktestToView,
      canAutotradeAlerts,

      canViewShowcaseApp,
      canViewPatternBuilderApp,
      canViewAlertApp,
      closeBacktestDialog,
      setCloseBacktestDialog,
      hasFeature,
      setAlertAutoTradeEnabled,
      autoTraderUsage,
      canAutotradeAlert,
      canEnableAutoTrader,
      backtestNameOverrides,
      renameBacktestLocally,
      alerts: alerts ?? [],
      setCopiedPattern,
    }),
    [
      canShowcaseViewBacktestSettings,
      canViewPrivatePatterns,
      canPrivatePatterns,
      activeTab,
      setActiveTabWithAccessControl,
      usermeta,
      backtestSettingInputData,
      setBacktestSettingInputData,
      isRunningBacktests,
      symbols,
      tradingHouse,
      setTradingHouse,
      canToggleTradingHouse,
      tradingHouseObject,
      canViewBacktestInsights,
      showReportCardDateTimeTooltips,
      isCreateAlertModalOpen,
      savedBacktests,
      savedBacktestsMap,
      canCreateAlerts,
      canActivateAlerts,
      canEditAlertTimeframes,
      canViewFeaturedBacktests,
      backtestToView,
      canAutotradeAlerts,

      canViewShowcaseApp,
      canViewPatternBuilderApp,
      canViewAlertApp,
      closeBacktestDialog,
      hasFeature,
      setAlertAutoTradeEnabled,
      autoTraderUsage,
      canAutotradeAlert,
      canEnableAutoTrader,
      backtestNameOverrides,
      renameBacktestLocally,
      alerts,
    ]
  )

  if (!user || !usermeta || !productTagsReady) return <LoadingShell />

  return (
    <ApplicationSuiteContext.Provider value={contextValue}>
      <BacktestProvider>
        <ModalProvider>
          {children}

          {shareableData && (
            <ShareableDialog shareableData={shareableData} setShareableData={setShareableData} />
          )}
          <PatternCopiedDialog pattern={copiedPattern} onClose={() => setCopiedPattern(null)} />
        </ModalProvider>
      </BacktestProvider>
    </ApplicationSuiteContext.Provider>
  )
}

function useApplicationSuiteContext(): ApplicationSuiteContextType {
  const context = useContext(ApplicationSuiteContext)
  if (!context) {
    throw new Error('useApplicationSuite must be used within an ApplicationSuiteProvider')
  }
  return context
}

export { ApplicationSuiteProvider, useApplicationSuiteContext }
